import {
  Button,
  ButtonProps,
  Checkbox,
  InputLabel,
  keyframes,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { LinkProps as RouterLinkProps } from 'react-router';

const waveAnimation = keyframes`
  0% {
    background-position: 0 0, 0% center;
  }
  100% {
    background-position: 0 0, 100% center;
  }
`;

export const AnimationAuthWrapperStyled = styled(Stack)({
  backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), url(${require('../../assets/images/background-light.png')})`,
  backgroundPosition: '0 0, 0% center',
  backgroundSize: 'auto, 200% auto',
  backgroundRepeat: 'no-repeat, repeat-x',
  boxShadow: '0 32px 20px 0 rgba(0, 0, 0, 0.04)',
  aspectRatio: '72/43',
  height: '100vh',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  justifyContent: 'center',
  alignItems: 'center',
  animation: `${waveAnimation} 30s linear infinite`,
});

export const SignUpWrapperStyled = styled(AnimationAuthWrapperStyled)(({ theme }) => ({
  padding: theme.spacing(4),
  justifyContent: 'flex-start',
}));

export const TitleStyled = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  lineHeight: '3rem',
  fontWeight: 700,
  color: theme.palette.primary[500],
}));

export const DescriptionStyled = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  lineHeight: '2rem',
  fontWeight: 700,
  color: theme.palette.secondary[900],
}));

export const FormWrapperStyled = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1.5),
  width: '100%',
  justifyContent: 'center',
}));

export const FormTextLabelStyled = styled(InputLabel)(({ theme }) => ({
  fontSize: '1rem',
  lineHeight: '1.125rem',
  fontWeight: 700,
  color: theme.palette.secondary[900],
  marginLeft: 0,
}));

export const TextFieldStyled = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 2),
    fontSize: '1rem',
    lineHeight: '1.125rem',
    color: theme.palette.secondary[900],
  },
  '& .MuiOutlinedInput-root fieldset': {
    borderColor: theme.palette.neutral[200],
  },
}));

export const ButtonStyled = styled(Button)(({ theme }) => ({
  height: theme.spacing(6),
  padding: theme.spacing(0, 4),
  color: theme.palette.neutral.main,
  fontSize: '1rem',
  lineHeight: '1.125rem',
  fontWeight: 700,
}));

export const FormSectionWrapperStyled = styled(Stack)({
  width: '100%',
  justifyContent: 'center',
});

export const FormFieldContainerStyled = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: theme.spacing(1),
}));

export const InfoTextStyled = styled(Typography)(({ theme }) => ({
  fontSize: '0.625rem',
  lineHeight: '1.125rem',
  fontWeight: 400,
  color: theme.palette.secondary[900],
}));

export const ContentWrapperStyled = styled(Stack)(({ theme }) => ({
  width: '670px',
  gap: theme.spacing(2.5),
}));

export const CheckboxStyled = styled(Checkbox)({
  padding: 0,
  width: 24,
  height: 24,
  '&.MuiCheckbox-root': {
    marginRight: '8px',
  },
});

export const UploadButtonStyled = styled(Button)<ButtonProps>({
  fontSize: '0.75rem',
  lineHeight: '1rem',
  fontWeight: 700,
  paddingLeft: '24px',
  paddingRight: '24px',
  '& input[type="file"]': {
    display: 'none',
  },
});

export const DocumentStyled = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  textDecoration: 'none',
  padding: theme.spacing(1),
  borderRadius: '4px',
  border: `1px solid ${theme.palette.secondary.main}`,
  backgroundColor: theme.palette.neutral.main,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

export const SizeStyled = styled(Typography)(({ theme }) => ({
  fontSize: '0.625rem',
  lineHeight: '0.75rem',
  color: theme.palette.secondary[100],
}));

export const TitleFileStyled = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textWrap: 'nowrap',
  fontSize: '0.75rem',
  lineHeight: '1rem',
  color: theme.palette.secondary[900],
}));

export const CompleteTitleStyled = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary[500],
}));

export const CompleteInfoStyled = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  lineHeight: '2rem',
  color: theme.palette.secondary[800],
  textAlign: 'center',
}));

export const CompleteButtonStyled = styled(Button)<ButtonProps & Omit<RouterLinkProps, 'variant'>>(
  ({ theme }) => ({
    borderRadius: theme.spacing(1),
    fontSize: '1rem',
    lineHeight: '1.125rem',
    fontWeight: 700,
    backgroundColor: theme.palette.primary[500],
  }),
);

export const CompletedWrapperStyled = styled(ContentWrapperStyled)(({ theme }) => ({
  width: '610px',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(5),
}));

export const SignUpContentStyled = styled(Stack)(({ theme }) => ({
  width: '100%',
  height: '100%',
  gap: theme.spacing(2.5),
  alignItems: 'center',
  overflow: 'hidden',
  overflowY: 'auto',
}));
